/**
 * Single source of truth for mapping an Encompass `le_fee` to its canonical Banker-Recon
 * line_type + warehouse classification. Used by auto-capture, dropdown exclusion, save,
 * reload, and tests — so identity never relies on ad-hoc name matching. No React, no I/O.
 */

import { getBankerReconLeFeeAtClosingTotal } from './leFeeAmounts';

export interface LeFeeShape {
  name?: string | null;
  section?: string | null;
  borrowerAmount?: number | null;
  sellerAmount?: number | null;
  paidTo?: string | null;
  paidToType?: string | null;
  totalAmount?: number | null;
  borrowerPTC?: number | string | null;
  borrowerOutsideClosing?: number | string | null;
}

/** An element of `encompass_loans.raw_data.fees[]` — the authoritative Encompass fee array. */
export interface RawEncompassFee {
  feeType?: string | null;     // e.g. "UserDefinedFee_813"
  description?: string | null; // e.g. "Trust Certification Fee"
  borPaidAmount?: number | null;
  [key: string]: unknown;
}

const num = (v: unknown): number => (Number.isFinite(Number(v)) ? Number(v) : 0);

/** Slug used by the existing dropdown derivation (`section_a_<slug>`). */
export const leFeeSlug = (name: string): string =>
  (name || '').trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');

/**
 * Resolve the AUTHORITATIVE display name for an le_fee. A generic "Line NNN" placeholder (the
 * field-mapped name when the fee's description field is empty — true for LW26042325's Line 813,
 * where field 369 is null) is resolved from `raw_data.fees[]`: find the element whose `feeType`
 * is `UserDefinedFee_NNN` and use its `description` (e.g. "Trust Certification Fee"). Falls back
 * to the le_fee's own name when no raw match exists.
 */
export function resolveAuthoritativeFeeName(
  fee: LeFeeShape,
  rawFees?: RawEncompassFee[] | null
): string {
  const name = (fee.name || '').trim();
  const lineMatch = name.match(/^line\s+(\d+)$/i);
  if (lineMatch && Array.isArray(rawFees)) {
    const feeType = `UserDefinedFee_${lineMatch[1]}`;
    const raw = rawFees.find((r) => ((r?.feeType as string) || '') === feeType);
    const desc = ((raw?.description as string) || '').trim();
    if (desc) {
      return desc;
    }
  }
  return name;
}

/**
 * Canonical `{ lineType, displayName }` for an le_fee, using `raw_data.fees[]` to resolve a
 * generic "Line NNN" name to its authoritative fee (e.g. Line 813 → "Trust Certification Fee" →
 * `section_a_trust_certification_fee`, which is allowlisted). Single source for identity + naming.
 */
export function canonicalizeLeFee(
  fee: LeFeeShape,
  rawFees?: RawEncompassFee[] | null
): { lineType: string; displayName: string } {
  const displayName = resolveAuthoritativeFeeName(fee, rawFees);
  return {
    lineType: `section_a_${leFeeSlug(displayName)}`,
    displayName
  };
}

/** Stable canonical line_type for an le_fee added via the dropdown (raw-fees aware). */
export function leFeeCanonicalLineType(fee: LeFeeShape, rawFees?: RawEncompassFee[] | null): string {
  return canonicalizeLeFee(fee, rawFees).lineType;
}

/** Dedicated revenue line_type for the seller/mixed-paid Lendwise Loan Discount (kept distinct from a
 * borrower-only dropdown `section_a_loan_discount`). */
export const LOAN_DISCOUNT_SELLER_PAID = 'loan_discount_seller_paid';

/** Stable line_type for the lender-owned Encompass Application Fee (Line 801b). */
export const APPLICATION_FEE_LINE_TYPE = 'section_a_application_fee';

const SELLER_PAID_AUTO_CAPTURE_LINE_TYPES: Record<string, string> = {
  'loan discount': LOAN_DISCOUNT_SELLER_PAID
};

/**
 * Retained Lendwise fees auto-captured as REVENUE rows (credited to net profit AND the warehouse
 * basis). Processing/Underwriting remain folded into the explicit scalars by
 * `deriveLendwiseProcessingUwScalars`.
 */
export const AUTO_CAPTURE_LINE_TYPES: Record<string, string> = {
  'application fee': APPLICATION_FEE_LINE_TYPE,
  ...SELLER_PAID_AUTO_CAPTURE_LINE_TYPES
};

/** Canonical line_types created by auto-capture — excluded from the dropdown's available set. */
export const AUTO_CAPTURE_CANONICAL_LINE_TYPES = new Set(Object.values(AUTO_CAPTURE_LINE_TYPES));

/**
 * Legacy warehouse-only auto line_types from the shipped "warehouse-only" version — the Processing/UW
 * `*_seller_paid_warehouse` rows. A recon saved under that version persisted them as amount-0 / retained>0
 * / nets rows; `mergeAutoCaptureRows` (b) cleans them so they don't double-count alongside the new P/UW
 * scalars. Line-type-scoped so a third-party pass-through (Trust Cert) is kept.
 *
 * `section_a_loan_discount` is deliberately NOT in this unconditional set: a THIRD-PARTY seller-paid Loan
 * Discount is a valid warehouse-only pass-through with the SAME shape (amount 0 / retained>0 / nets). The
 * OLD Lendwise warehouse-only discount is removed instead by the supersession branch (c) — which fires
 * only when a Lendwise seller discount actually auto-captures — so the third-party pass-through survives.
 */
export const LEGACY_WAREHOUSE_ONLY_AUTO_LINE_TYPES = new Set([
  'processing_fee_seller_paid_warehouse',
  'underwriting_fee_seller_paid_warehouse'
]);

const LENDWISE_PAID_TO = 'lendwise mortgage, inc.';

/** Predicate: the fee is retained by Lendwise (payer = Lender, or payee normalizes to Lendwise). */
export function isPaidToLendwise(fee: LeFeeShape): boolean {
  return (
    (fee.paidToType || '').trim().toLowerCase() === 'lender' ||
    (fee.paidTo || '').trim().toLowerCase() === LENDWISE_PAID_TO
  );
}

const SCALAR_OWNED_FEE_NAMES = new Set(['processing fee', 'underwriting fee']);

/**
 * Processing/Underwriting paid to Lendwise are owned by the explicit recon scalars
 * (`deriveLendwiseProcessingUwScalars`), so they must be excluded from the Section A/B fee dropdown —
 * otherwise the user could add a duplicate revenue line on top of the scalar (double-count).
 */
export function isLendwiseScalarOwnedFee(fee: LeFeeShape): boolean {
  return SCALAR_OWNED_FEE_NAMES.has((fee.name || '').trim().toLowerCase()) && isPaidToLendwise(fee);
}

/**
 * Seller-paid Loan Discount predicate: require `sellerAmount > 0` and a Lendwise payee. Application
 * Fee intentionally follows the separate lender-owned rule in `autoCaptureLendwiseRevenueLineType`.
 */
export function autoCaptureSellerPaidLendwiseLineType(fee: LeFeeShape): string | null {
  const lineType = SELLER_PAID_AUTO_CAPTURE_LINE_TYPES[(fee.name || '').trim().toLowerCase()];
  if (!lineType) {
    return null;
  }
  if (num(fee.sellerAmount) <= 0) {
    return null;
  }
  if (!isPaidToLendwise(fee)) {
    return null;
  }
  return lineType;
}

/**
 * Single classifier for every authoritative Lendwise revenue row derived from le_fees.
 *
 * Application Fee is intentionally payee-blind: Line 801b is lender-owned in Lendwise's
 * correspondent workflow, and blank or lagging Encompass payee metadata must not suppress real
 * revenue. Any impossible cash-treatment mismatch remains visible in the warehouse-wire audit.
 * Loan Discount keeps its existing seller-paid + Lendwise-payee eligibility rule.
 */
export function autoCaptureLendwiseRevenueLineType(fee: LeFeeShape): string | null {
  const normalizedName = (fee.name || '').trim().toLowerCase();
  if (
    fee.section === 'A' &&
    normalizedName === 'application fee' &&
    getBankerReconLeFeeAtClosingTotal(fee) > 0
  ) {
    return APPLICATION_FEE_LINE_TYPE;
  }

  return autoCaptureSellerPaidLendwiseLineType(fee);
}

/** A Lendwise REVENUE row auto-captured from le_fees (amount = full Lendwise fee; warehouse_retained 0). */
export interface AutoCaptureRow {
  id: string;
  description: string;
  amount: number;
  warehouse_retained_amount: number;
  nets_to_warehouse_advance: true;
  reimbursable: false;
}

/**
 * Derive authoritative Lendwise REVENUE rows from le_fees. Application Fee uses the shared
 * at-closing basis and a fixed 801b description. Loan Discount retains its full borrower + seller
 * basis. Each canonical line_type is emitted at most once even if malformed source data duplicates
 * a fee row.
 */
export function deriveAutoCaptureRows(leFees: LeFeeShape[] | null | undefined): AutoCaptureRow[] {
  const rows: AutoCaptureRow[] = [];
  const emittedLineTypes = new Set<string>();

  for (const fee of leFees || []) {
    const lineType = autoCaptureLendwiseRevenueLineType(fee);
    if (!lineType || emittedLineTypes.has(lineType)) {
      continue;
    }

    rows.push({
      id: lineType,
      description: lineType === APPLICATION_FEE_LINE_TYPE
        ? 'Application Fee (801b)'
        : (fee.name || lineType) as string,
      amount: lineType === APPLICATION_FEE_LINE_TYPE
        ? getBankerReconLeFeeAtClosingTotal(fee)
        : num(fee.borrowerAmount) + num(fee.sellerAmount),
      warehouse_retained_amount: 0,
      nets_to_warehouse_advance: true,
      reimbursable: false
    });
    emittedLineTypes.add(lineType);
  }

  return rows;
}

/**
 * Idempotently upsert the auto-captured Lendwise revenue rows by canonical line_type. Used at load AND
 * before save/finalize so the rows are authoritative and cannot be dropped by edits. Removes:
 *  (a) current auto rows (canonical line_type) — re-derived below;
 *  (b) LEGACY warehouse-only Processing/UW rows from the shipped version (line_type ∈ legacy set AND
 *      amount 0 / retained>0 / nets) — line-type-scoped so the Trust Cert AND a third-party Loan Discount
 *      pass-through are NOT deleted;
 *  (c) any `section_a_loan_discount` (incl. amount>0) ONLY when a seller/mixed LENDWISE discount is
 *      auto-capturing — supersedes both a borrower-paid dropdown row and the OLD Lendwise warehouse-only
 *      discount (now consolidated into `loan_discount_seller_paid`). A THIRD-PARTY seller-paid discount has
 *      no Lendwise auto-capture, so it survives here as a warehouse-only pass-through.
 */
export function mergeAutoCaptureRows<
  T extends {
    id: string;
    amount?: number | null;
    warehouse_retained_amount?: number | null;
    nets_to_warehouse_advance?: boolean | null;
  }
>(loanRevenue: T[], leFees: LeFeeShape[] | null | undefined): (T | AutoCaptureRow)[] {
  const autoRows = deriveAutoCaptureRows(leFees);
  const hasSellerDiscount = autoRows.some((r) => r.id === LOAN_DISCOUNT_SELLER_PAID);
  const retainedRows = loanRevenue.filter((item) => {
    // (a) current auto rows — re-derived below
    if (AUTO_CAPTURE_CANONICAL_LINE_TYPES.has(item.id)) {
      return false;
    }
    // (b) stale shipped-version warehouse-only auto rows (line-type-scoped → keeps Trust Cert)
    if (
      LEGACY_WAREHOUSE_ONLY_AUTO_LINE_TYPES.has(item.id) &&
      num(item.amount) === 0 &&
      num(item.warehouse_retained_amount) > 0 &&
      item.nets_to_warehouse_advance === true
    ) {
      return false;
    }
    // (c) supersede section_a_loan_discount (borrower-paid dropdown OR old Lendwise warehouse-only) ONLY
    // when a Lendwise seller/mixed discount auto-captures and owns the fee. A THIRD-PARTY seller-paid
    // section_a_loan_discount has hasSellerDiscount=false (not Lendwise → not auto-captured) → it survives.
    if (hasSellerDiscount && item.id === 'section_a_loan_discount') {
      return false;
    }
    return true;
  });
  const applicationRows = autoRows.filter((row) => row.id === APPLICATION_FEE_LINE_TYPE);
  const remainingAutoRows = autoRows.filter((row) => row.id !== APPLICATION_FEE_LINE_TYPE);

  return [
    ...applicationRows,
    ...retainedRows,
    ...remainingAutoRows
  ];
}

/** Loan columns used as the borrower-only fallback when an LE fee row is absent. */
export interface ProcessingUwFallback {
  fees_processing?: number | null;
  fees_underwriting?: number | null;
}

/**
 * Single source of truth for the Processing/Underwriting revenue scalars. Each = `borrowerAmount +
 * (sellerAmount ONLY when the LE fee is paid to Lendwise)` — so a seller-paid Lendwise fee is credited
 * to net profit AND the warehouse basis, while a non-Lendwise Processing/UW stays borrower-only. Falls
 * back to the loan's `fees_processing`/`fees_underwriting` columns (borrower-only) when the LE fee is
 * absent. MUST be used at loan load AND before save/finalize so the values cannot diverge.
 */
export function deriveLendwiseProcessingUwScalars(
  leFees: LeFeeShape[] | null | undefined,
  loan: ProcessingUwFallback | null | undefined
): { processing: number; underwriting: number } {
  const fold = (feeName: string, fallback: number): number => {
    const fee = (leFees || []).find((f) => f.section === 'A' && (f.name || '') === feeName);
    if (!fee) {
      return num(fallback);
    }
    return num(fee.borrowerAmount) + (isPaidToLendwise(fee) ? num(fee.sellerAmount) : 0);
  };
  return {
    processing: fold('Processing Fee', num(loan?.fees_processing)),
    underwriting: fold('Underwriting Fee', num(loan?.fees_underwriting))
  };
}

// ============================================================================
// Conventional upfront MI premium — "Warehouse withheld MI"
// ============================================================================

/**
 * Line type of the Closing Disclosure Section F "Mortgage Insurance Premium" row on a Conventional
 * loan when the warehouse withheld the premium for Lendwise to pay the insurer. The row is ordinary
 * company REVENUE that nets to the warehouse advance (the warehouse returned the cash inside the
 * net-proceeds wire); `buildLineItemsFromState` derives the matching company EXPENSE
 * (`MI_PREMIUM_REMITTANCE_LINE_TYPE`), so net profit is unchanged and both legs show on the company
 * card and the accounting export. Never allowlisted: it enters the warehouse basis through
 * `nets_to_warehouse_advance: true` only. The team tracks the actual payment to the insurer.
 */
export const MORTGAGE_INSURANCE_PREMIUM_LINE_TYPE = 'section_f_mortgage_insurance_premium';

/** Company expense leg (category `lender_cost`): the premium Lendwise pays the insurer outside the wire. */
export const MI_PREMIUM_REMITTANCE_LINE_TYPE = 'mi_premium_remittance';

export const MI_PREMIUM_ROW_DESCRIPTION = 'Mortgage Insurance Premium';

export function isMiPremiumLineType(id: unknown): boolean {
  return id === MORTGAGE_INSURANCE_PREMIUM_LINE_TYPE;
}

export function miPremiumExpenseDescription(payee?: string | null): string {
  return `Mortgage Insurance Premium paid to ${(payee || '').trim() || 'insurer'}`;
}

export interface ConventionalMiPremiumFee {
  fee: LeFeeShape;
  /** Borrower + seller dollars collected through closing (Section F carries no timing fields). */
  amount: number;
  /** Payee as printed on the Closing Disclosure (e.g. "MGIC"); "third party" when blank. */
  payee: string;
}

const MI_PREMIUM_FEE_NAME = 'mortgage insurance premium';
const MI_PREMIUM_MONEY_TOLERANCE = 0.01;

function roundMoney(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

/**
 * The third-party Section F Mortgage Insurance Premium on a Conventional loan, or null. Deliberately
 * narrow: FHA premiums belong to the FHA UFMIP bridge, VA/USDA fees are government funding
 * pass-throughs, and a premium paid to Lendwise is not a pass-through at all.
 */
export function findConventionalMiPremiumFee(
  leFees: LeFeeShape[] | null | undefined,
  loanType: string | null | undefined
): ConventionalMiPremiumFee | null {
  if ((loanType || '').trim().toUpperCase() !== 'CONVENTIONAL') {
    return null;
  }
  for (const fee of leFees || []) {
    if ((fee.section || '').trim().toUpperCase() !== 'F') {
      continue;
    }
    if ((fee.name || '').trim().toLowerCase() !== MI_PREMIUM_FEE_NAME) {
      continue;
    }
    if (isPaidToLendwise(fee)) {
      continue;
    }
    const amount = roundMoney(getBankerReconLeFeeAtClosingTotal(fee));
    if (amount <= MI_PREMIUM_MONEY_TOLERANCE) {
      continue;
    }
    return { fee, amount, payee: (fee.paidTo || '').trim() || 'third party' };
  }
  return null;
}

/** The loan-revenue row shape the helpers read and write (structural; `LoanRevenue` fits). */
export interface MiPremiumRevenueRow {
  id: string;
  description: string;
  amount: number;
  reimbursable?: boolean;
  warehouse_retained_amount?: number;
  nets_to_warehouse_advance?: boolean | null;
}

/** The canonical "Warehouse withheld MI" revenue row: revenue = premium, nets to the warehouse advance. */
export function buildMiPremiumRevenueRow(amount: number): MiPremiumRevenueRow {
  return {
    id: MORTGAGE_INSURANCE_PREMIUM_LINE_TYPE,
    description: MI_PREMIUM_ROW_DESCRIPTION,
    amount: roundMoney(amount),
    warehouse_retained_amount: 0,
    nets_to_warehouse_advance: true,
    reimbursable: false
  };
}

export function isMiPremiumWithheld(rows: ReadonlyArray<{ id: string }>): boolean {
  return rows.some((row) => isMiPremiumLineType(row.id));
}

function isCanonicalMiPremiumRow(row: MiPremiumRevenueRow, amount: number): boolean {
  return row.description === MI_PREMIUM_ROW_DESCRIPTION
    && Math.abs(num(row.amount) - roundMoney(amount)) <= MI_PREMIUM_MONEY_TOLERANCE
    && num(row.warehouse_retained_amount) === 0
    && row.nets_to_warehouse_advance === true
    && row.reimbursable !== true;
}

/**
 * Check or uncheck "Warehouse withheld MI". Checked → exactly one canonical premium row at `amount`
 * (stale or duplicate rows are rebuilt); unchecked → no premium row. Returns the same array when
 * nothing changes.
 */
export function setMiPremiumWithheld<T extends MiPremiumRevenueRow>(
  rows: T[],
  withheld: boolean,
  amount: number
): T[] {
  const miRows = rows.filter((row) => isMiPremiumLineType(row.id));
  const others = rows.filter((row) => !isMiPremiumLineType(row.id));
  if (!withheld) {
    return miRows.length === 0 ? rows : others;
  }
  if (miRows.length === 1 && isCanonicalMiPremiumRow(miRows[0], amount)) {
    return rows;
  }
  return [...others, { ...(miRows[0] ?? {}), ...buildMiPremiumRevenueRow(amount) } as T];
}

/**
 * Keep an existing premium row canonical and on the CURRENT Closing Disclosure amount (draft load and
 * save). Never adds a row: whether the warehouse withheld the premium is the reviewer's call.
 */
export function syncMiPremiumRow<T extends MiPremiumRevenueRow>(
  rows: T[],
  eligible: ConventionalMiPremiumFee | null
): T[] {
  const miRows = rows.filter((row) => isMiPremiumLineType(row.id));
  if (miRows.length === 0) {
    return rows;
  }
  return setMiPremiumWithheld(rows, true, eligible ? eligible.amount : num(miRows[0].amount));
}
